<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { storeToRefs } from 'pinia'
import BModal from '@/components/common/BModal.vue'
import BButton from '@/components/common/bButton.vue'
import BCheckbox from '@/components/common/bCheckbox.vue'
import { DEFAULT_UNREAD_EMAIL_DAYS, useChatStore } from '@/config/store/chatStore'
import MaterialSymbolsAddRounded from '~icons/material-symbols/add-rounded'
import MaterialSymbolsCloseRounded from '~icons/material-symbols/close-rounded'

const { t } = useI18n()
const chatStore = useChatStore()
const { unreadEmailEnabled, unreadEmailDays } = storeToRefs(chatStore)

const isOpen = defineModel<boolean>({ default: false })

const enabled = ref(true)
const days = ref<number[]>([...DEFAULT_UNREAD_EMAIL_DAYS])
const saving = ref(false)
const errorMessage = ref('')

watch(isOpen, (open) => {
  if (!open) return
  enabled.value = unreadEmailEnabled.value
  days.value = [...unreadEmailDays.value]
  errorMessage.value = ''
})

const canSave = computed(() => {
  if (!enabled.value) return true
  return days.value.length >= 1 && days.value.every(day => day >= 1 && day <= 90)
})

const addDay = () => {
  if (days.value.length >= 5) return
  const last = days.value[days.value.length - 1] || 1
  days.value = [...days.value, Math.min(90, last + 2)]
}

const removeDay = (index: number) => {
  if (days.value.length <= 1) return
  days.value = days.value.filter((_, i) => i !== index)
}

const resetDefaults = () => {
  days.value = [...DEFAULT_UNREAD_EMAIL_DAYS]
}

const save = async () => {
  if (!canSave.value || saving.value) return
  saving.value = true
  errorMessage.value = ''
  try {
    await chatStore.persistChatSettings({
      unreadEmailEnabled: enabled.value,
      unreadEmailDays: enabled.value
        ? chatStore.normalizeUnreadEmailDays(days.value)
        : unreadEmailDays.value,
    })
    isOpen.value = false
  }
  catch {
    errorMessage.value = t('modules.chat.settings.saveError')
  }
  finally {
    saving.value = false
  }
}
</script>

<template>
  <BModal
    v-model="isOpen"
    position="base"
    class="w-[min(440px,95dvw)]"
    data-cy="chat-settings-modal"
  >
    <template #header>
      <h2
        class="font-semibold"
        data-cy="chat-settings-title"
      >
        {{ t('modules.chat.settings.title') }}
      </h2>
    </template>

    <div class="space-y-4 p-1">
      <p class="text-sm opacity-70 leading-relaxed">
        {{ t('modules.chat.settings.emailHelp') }}
      </p>

      <div data-cy="chat-settings-email-enabled">
        <BCheckbox
          v-model="enabled"
          :label="t('modules.chat.settings.emailEnabled')"
          position="left"
        />
      </div>

      <div
        v-if="enabled"
        class="space-y-2 rounded-xl border border-black/10 dark:border-white/15 p-3"
        data-cy="chat-settings-days"
      >
        <div class="flex items-center justify-between gap-2">
          <div class="text-sm font-medium">
            {{ t('modules.chat.settings.daysTitle') }}
          </div>
          <button
            type="button"
            class="text-xs text-primary-500 hover:underline"
            data-cy="chat-settings-reset-defaults"
            @click="resetDefaults"
          >
            {{ t('modules.chat.settings.resetDefaults') }}
          </button>
        </div>
        <p class="text-xs opacity-60">
          {{ t('modules.chat.settings.daysHelp') }}
        </p>

        <div
          v-for="(_, index) in days"
          :key="index"
          class="flex items-center gap-2"
        >
          <span class="text-sm opacity-70 w-10 shrink-0">
            J+
          </span>
          <input
            v-model.number="days[index]"
            type="number"
            min="1"
            max="90"
            :data-cy="`chat-settings-day-${index}`"
            class="w-20 rounded-lg border border-black/10 dark:border-white/15 bg-transparent px-2 py-1.5 text-sm"
          >
          <button
            v-if="days.length > 1"
            type="button"
            class="p-1 rounded-sm bg-hover"
            :data-cy="`chat-settings-day-remove-${index}`"
            :title="t('common.delete')"
            @click="removeDay(index)"
          >
            <MaterialSymbolsCloseRounded class="w-4 h-4" />
          </button>
        </div>

        <button
          v-if="days.length < 5"
          type="button"
          class="inline-flex items-center gap-1 text-xs text-primary-500 hover:underline"
          data-cy="chat-settings-add-day"
          @click="addDay"
        >
          <MaterialSymbolsAddRounded class="w-4 h-4" />
          {{ t('modules.chat.settings.addDay') }}
        </button>
      </div>

      <p
        v-if="errorMessage"
        class="text-sm text-error"
      >
        {{ errorMessage }}
      </p>
    </div>

    <template #footer>
      <div class="flex justify-end gap-2">
        <BButton
          bg="base"
          data-cy="chat-settings-cancel"
          @click="isOpen = false"
        >
          {{ t('common.cancel') }}
        </BButton>
        <BButton
          bg="primary"
          data-cy="chat-settings-save"
          :disabled="!canSave || saving"
          @click="save"
        >
          {{ t('common.save') }}
        </BButton>
      </div>
    </template>
  </BModal>
</template>
